import apiClient from '../lib/axios';

/** The account fields the app caches for the signed-in user. */
export interface AccountSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'USER';
  twoFactorEnabled: boolean;
}

/**
 * Email 2FA settings for the signed-in user.
 *
 * Turning it on is two calls on purpose. `startSetup` emails a code and changes
 * nothing; 2FA only becomes active once `confirmSetup` accepts that code. That
 * way a wrong address or a broken mail server cannot lock someone out of their
 * own account.
 */
class TwoFactorService {
  /** Emails a verification code. Returns the address it went to. */
  async startSetup(): Promise<{ email: string }> {
    const response = await apiClient.post('/auth/2fa/setup/start');
    return response.data.data;
  }

  /** Verifies the code and turns 2FA on. Returns the updated account. */
  async confirmSetup(code: string): Promise<AccountSummary> {
    const response = await apiClient.post('/auth/2fa/setup/confirm', { code });
    return response.data.data;
  }

  /** Turns 2FA off. No code needed, the caller is already signed in. */
  async disable(): Promise<AccountSummary> {
    const response = await apiClient.post('/auth/disable-email-2fa');
    return response.data.data;
  }

  /**
   * Resend the code during login. Returns a replacement tempSessionId, because
   * the server reissues the session alongside the new code so the two expire
   * together. The caller must store it in place of the one it holds.
   */
  async resendLoginCode(tempSessionId: string): Promise<{ tempSessionId: string }> {
    const response = await apiClient.post('/auth/send-2fa-code', { tempSessionId });
    return response.data.data;
  }
}

export const twoFactorService = new TwoFactorService();
